import React from "react";
import { ConfigProvider, Spin, Button } from "antd";
import {
  CrownOutlined,
  ThunderboltOutlined,
  DownloadOutlined,
  UndoOutlined,
  QrcodeOutlined,
} from "@ant-design/icons";
import "antd/dist/reset.css";
import { useQRGenerator } from "../../hooks/useQRGenerator";
import { useAccountDetails } from "../../hooks/useAccountDetails";
import { getPortalId,getSlug } from "../../utils/portalId";
import UrlInput from "../UrlInput/UrlInput";
import CustomizePanel from "../CustomizePanel/CustomizePanel";
import QRPreview from "../QRPreview/QRPreview";
import "./QRCodeGenerator.css";
import { setAuthToken, getAuthToken, isAuthenticated } from '../../utils/authToken';
/**
 * Root container for the QR Code Generator UI.
 * Designed to be embedded inside a HubSpot modal iframe at 1280×720.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────┐
 *   │  Header  (title + plan/usage strip)              │
 *   ├──────────────────────┬───────────────────────────┤
 *   │  Left: URL input +   │  Right: QR preview        │
 *   │  Customize panel     │  + download actions       │
 *   └──────────────────────┴───────────────────────────┘
 */
const QRCodeGenerator = () => {
  const portalId = getPortalId();
  const slug = getSlug();
 // Assuming the token is stored in localStorage after authentication
  // Fetch account plan + saved settings in parallel
  const {
    account,
    settings,
    uploads,
    loading: accountLoading,
    error: accountError,
  } = useAccountDetails(portalId, slug);
  // Generator state — pre-populated from saved settings once they load
  const {
    url,
    size,
    margin,
    errorCorrectionLevel,
    roundBlockSizeMode,
    foregroundColor,
    backgroundColor,
    logo,
    logoId,
    logoInfo,
    logoResizeToWidth,
    logoPunchoutBackground,
    labelText,
    labelAlignment,
    labelFontSize,
    labelColor,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    qrImageUrl,
    isLoading,
    updateField,
    generateQR,
    downloadQR,
    resetQR,
    uploadLogo,
  } = useQRGenerator({ portalId, initialSettings: settings });

  const usage = account?.usage;
  const usedPct = usage ? Math.round((usage.used / usage.allowed) * 100) : 0;

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#3b82f6",
          borderRadius: 8,
          fontFamily: "'Gabarito', sans-serif",
        },
      }}
    >
      <div className="qr-generator-root">
        {/* ── Body — two-column layout ── */}
        <div className="qr-body">
          {/* Left panel */}
          <div className="qr-left-panel">
            <UrlInput
              value={url}
              onChange={updateField}
              onGenerate={generateQR}
              isLoading={isLoading}
            />

            {/* <div className="qr-divider" /> */}

            <div className="customize-section-label">
              Customize your QR code
            </div>

            <CustomizePanel
              size={size}
              margin={margin}
              errorCorrectionLevel={errorCorrectionLevel}
              roundBlockSizeMode={roundBlockSizeMode}
              foregroundColor={foregroundColor}
              backgroundColor={backgroundColor}
              logo={logo}
              logoId={logoId}
              logoInfo={logoInfo}
              logoResizeToWidth={logoResizeToWidth}
              logoPunchoutBackground={logoPunchoutBackground}
              labelText={labelText}
              labelAlignment={labelAlignment}
              labelFontSize={labelFontSize}
              labelColor={labelColor}
              utmSource={utmSource}
              utmMedium={utmMedium}
              utmCampaign={utmCampaign}
              utmContent={utmContent}
              utmTerm={utmTerm}
              onFieldChange={updateField}
              onUploadLogo={uploadLogo}
              uploadedFiles={uploads}
            />

            <div className="qr-actions qr-actions--row">
              <Button
                className="download-btn"
                type="primary"
                size="large"
                icon={<QrcodeOutlined />}
                loading={isLoading}
                onClick={generateQR}
              >
                Generate QR Code
              </Button>
              <Button
                className="reset-btn"
                size="large"
                icon={<UndoOutlined />}
                disabled={isLoading}
                onClick={resetQR}
              >
                Reset
              </Button>
            </div>
          </div>

          {/* Right panel */}
          <div className="qr-right-panel">
            <QRPreview
              qrImageUrl={qrImageUrl}
              isLoading={isLoading}
              onDownload={downloadQR}
              onReset={resetQR}
              previewOnly
            />
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default QRCodeGenerator;
